import React, {useCallback, useEffect, useRef, useState} from "react";
import Container from "react-bootstrap/Container";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Button from "@material-ui/core/Button";
import FormLabel from "@mui/material/FormLabel";
import Alert from "@mui/material/Alert";
import Stack from "@mui/material/Stack";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  validateServiceLine,
  validateEmailAddress,
  validateFirstName,
  validateLastName,
  validateOrganization,
  validateRole,
  validateOrbit,
} from "./validation.js";
import Select from "@material-ui/core/Select";
import { InputWithErrorMessage } from "../forms/inputWithErrorMessage.js";
import styles from "./userinvite.module.scss";
import { getOrbitList, getUser, setUser } from "../../helpers/index.js";
import { orgList } from "../../actions/orgList.js";
import {
  SERVICE_LINE_ADMIN,
  ORBIT_ADMIN,
  ORGANIZATION_ADMIN,
  REGULAR_USER,
  SYS_ADMIN,
  ROLE_KEY,
  roleIdsToKeys, hasRole,
} from "../../constants/roles.js";
import { diagnosisUserList } from "../../actions/diagnosisUserList.js";
import {
  getServiceLineList,
  serviceLineList,
} from "../../actions/serviceLineList.js";
import { rolesList } from "../../actions/rolesList.js";
import { relativeList } from "../../actions/relativeList.js";
import { inviteProfessional } from "../../actions/inviteProfessional.js";
import {
  validateDiagnoses,
  validateRelation,
} from "../patientinvite/validation.js";
import { invitePatient } from "../../actions/invitePatient.js";
import { inviteFamily } from "../../actions/inviteFamily.js";
import { ToastContainer, toast } from "react-toastify";
import { AUTH_ERROR, REGISTER_ERROR } from "../../actions/types.js";
import {
  setDiagnosticFilterList,
  setDiagnosticList,
  setDisplayDiagnostics,
  setDisplayOrbit,
  setDisplayOrganization,
  setDisplayServiceLine,
  setDisplayTiles,
  setInitialTopNavDisplay,
  setOrbitList,
  setOrganizationList,
} from "../../actions/home";
import { loadOrbitList } from "../common/home/index";
import { orbit } from "../../actions/orbit";
import Card from "react-bootstrap/Card";
import { CardContent } from "@mui/material";
import item from "babel-loader/lib/Error";
import Modal from "@mui/material/Modal";
import Typography from "@mui/material/Typography";
import {ALREADY_ACTIVATED, RESEND_INVITE} from "../../constants/inviteAction";

// Attaches a native 'change' listener via callback ref so programmatic value
// changes (Playwright, autofill, assistive tech) update React state even when
// React 17's synthetic onChange is not triggered.
function useNativeChangeRef(handler) {
    const infoRef = useRef(null);
    const handlerRef = useRef(handler);
    handlerRef.current = handler;

    const callbackRef = useCallback((el) => {
        if (infoRef.current) {
            infoRef.current.el.removeEventListener('change', infoRef.current.fn);
            infoRef.current = null;
        }
        if (el) {
            const fn = () => handlerRef.current(el.value);
            el.addEventListener('change', fn);
            infoRef.current = { el, fn };
        }
    }, []);
    return callbackRef;
}

const PatientInvite = () => {
  const user = getUser();
  const formRef = useRef(null);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [emailAddress, setEmailAddress] = useState("");
  const [invitedRole, setInvitedRole] = useState("");
  const [roleList, setRoleList] = useState([]);
  const [relationList, setRelationList] = useState([]);
  const [roleTempList, setRoleTempList] = useState([]);
  const [diagnoses, setDiagnoses] = useState([]);
  const [diagnosesError, setDiagnosesError] = useState("");
  const [diagnosisList, setDiagnosisList] = useState([]);
  const [organizationId, setOrganizationId] = useState("");
  const [organizationList, setOrganizationList] = useState([]);
  const [serviceLineId, setServiceLineId] = useState("");
  const [serviceLineList, setServiceLineList] = useState([]);
  const [orbitList, setOrbitList] = useState([]);
  const [serviceLineListModified, setServiceLineListModified] = useState(false);
  const [orbitId, setOrbitId] = useState("");
  const orgNativeRef = useNativeChangeRef((value) => {
      setOrganizationId(prev => value !== (prev || "") ? value : prev);
  });
  const serviceLineNativeRef = useNativeChangeRef((value) => {
      setServiceLineId(prev => {
          const current = Array.isArray(prev) ? prev[0] : prev;
          return value !== (current || "") ? (value ? [value] : "") : prev;
      });
  });
  const orbitNativeRef = useNativeChangeRef((value) => {
      setOrbitId(prev => value !== prev ? value : prev);
  });
  const diagnosisNativeRef = useNativeChangeRef((value) => {
      setDiagnoses(prev => {
          const current = Array.isArray(prev) && prev.length > 0 ? String(prev[0]) : "";
          return value !== current ? (value ? [parseInt(value)] : []) : prev;
      });
  });
  const [firstNameError, setFirstNameError] = useState("");
  const [lastNameError, setLastNameError] = useState("");
  const [emailAddressError, setEmailAddressError] = useState("");
  const [serviceLineError, setServiceLineError] = useState("");
  const [roleError, setRoleError] = useState("");
  const [orbitError, setOrbitError] = useState("");
  const [organizationIdError, setOrganizationIdError] = useState("");
  const [relationError, setRelationError] = useState("");
  const [isDirty, setIsDirty] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [relation, setRelation] = useState(0);
  const { errorMessage } = useSelector((state) => state.register);
  const [userMessage, setUserMessage] = useState("");
  const [firstNameLabel, setFirstNameLabel] = useState("");
  const [lastNameLabel, setLastNameLabel] = useState("");
  const [firstNameHelperText, setFirstNameHelperText] = useState("");
  const [lastNameHelperText, setLastNameHelperText] = useState("");
  const [emailAddressHelperText, setEmailAddressHelperText] = useState("");
  const [titleText, setTitleText] = useState("Family Member");
  const [titleTextTop, setTitleTextTop] = useState("a Family Member");
  const [subTitleText, setSubTitleText] = useState("family member");
  const [isUserOrbitAdmin, setIsUserOrbitAdmin] = useState(false);
  const [resendInvite, setResendInvite] = useState(false);
  const [tempEmailAddress, setTempEmailAddress] = useState("");
  const [alreadyActivatedIsActive, setAlreadyActivatedIsActiveIsActive] =
      useState(false);
  const [open, setOpen] = React.useState(false);
  const [alreadyActivatedOpen, setAlreadyActivatedOpen] = React.useState(false);
  const [
    userTriggeredAlreadyActivatedModal,
    setUserTriggeredAlreadyActivatedModal,
  ] = useState(false);
  const { defaultOrbit, organizationId: defaultOrganizationId } = useSelector(
    (state) => {
      return state.home;
    },
  );

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleClick = (event) => {
    setResendInvite(true);
    setTimeout(() => {
      formRef.current.requestSubmit();
    }, 0);
  };
  const handleAlreadyActivatedClick = (event) => {
    setUserTriggeredAlreadyActivatedModal(true);
    setTempEmailAddress(emailAddress);
    setResendInvite(false);
    setEmailAddress("");
    setFirstName("");
    setLastName("");
    setOrganizationId("");
    setDiagnoses([]);
    handleAlreadyActivatedClose();
  };

  const handleOpen = () => {
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
  };

  const handleAlreadyActivatedOpen = () => {
    setAlreadyActivatedOpen(true);
  };
  const handleAlreadyActivatedClose = () => {
    setAlreadyActivatedOpen(false);
  };

  useEffect(() => {
    if (errorMessage || errorMessage.length > 0) {
      toast.error(errorMessage);
    }
  }, [errorMessage]);

  useEffect(() => {
    if (userMessage || userMessage.length > 0) {
      toast.success(userMessage);
    }
  }, [userMessage]);

  const loadOrganization = () => {
    (async () => {
      const ol = await orgList({}, (data) => {
        setOrganizationList(
          data.organizations.map((organization) => ({
            organizationId: organization.organizationId,
            orgName: organization.orgName,
          })),
        );
      });

      await dispatch(ol);
    })();
  };

  const submitForm = async (e) => {
    let ip = null;
    dispatch({
      type: REGISTER_ERROR,
      payload: "",
    });
    e.preventDefault();

    if (!(await validateAllFields())) return;

    if (!user?.roles?.includes(REGULAR_USER)) {
      const numericRole = user?.roles?.includes(ORBIT_ADMIN)
        ? [ORBIT_ADMIN]
        : [parseInt(invitedRole)];

      ip = await inviteProfessional(
        {
          firstName,
          lastName,
          emailAddress,
          orbitId,
          role: numericRole,
          roleKey: roleIdsToKeys(numericRole),
          serviceLineId:
              serviceLineId &&
              Array.isArray(serviceLineId) &&
              serviceLineId.length > 0
                  ? serviceLineId[0]
                  : serviceLineId && serviceLineId.length > 0
                      ? serviceLineId
                      : undefined,
          organizationId,
          resendInvite,
          diagnoses:
            diagnoses && Array.isArray(diagnoses) && diagnoses.length > 0
              ? diagnoses
              : diagnoses && diagnoses.length > 0
                ? [diagnoses]
                : undefined,
        },
        (data) => {
          if (data.inviteAction && data.inviteAction === RESEND_INVITE) {
            handleOpen();
          } else if (
              data.inviteAction &&
              data.inviteAction === ALREADY_ACTIVATED
          ) {
            handleAlreadyActivatedOpen();
          } else {
            setUserMessage("Invitation Sent!");

            setTimeout(() => {
              navigate("/");
            }, 3000);
          }
        },
      );
    } else {
      ip = await inviteFamily(
        {
          firstName,
          lastName,
          emailAddress,
          orbitId: orbitId && orbitId ? orbitId : defaultOrbit,
          relation: parseInt(relation),
          roleKey: [ROLE_KEY.REGULAR_USER],
          diagnoses:
            user?.diagnoses && user?.diagnoses.length > 0
              ? user?.diagnoses
              : undefined,
          organizationId:
            organizationId && organizationId.length > 0
              ? organizationId
              : user?.organizationId,
          serviceLineId:
              serviceLineId &&
              Array.isArray(serviceLineId) &&
              serviceLineId.length > 0
                  ? serviceLineId[0]
                  : serviceLineId && serviceLineId.length > 0
                      ? serviceLineId
                      : undefined
        },
        (data) => {
          if (data?.errors) return;
          setUserMessage("Invitation Sent!");
          navigate("/");
          // setTimeout(() => {
          //     navigate('/');
          // }, 3000);
        },
      );
    }
    await dispatch(ip);
  };

  const swapList = async (list1, list2) => {
    const index1 = list1.findIndex(
      (item2) => item2.roleId === SERVICE_LINE_ADMIN,
    );
    const index2 = list2.findIndex(
      (item2) => item2.roleId === SERVICE_LINE_ADMIN,
    );

    /*
        if (!isInitialLoad) {
            if (window.confirm('You are about to leave the page.  Do you wish to continue?')) {
                navigate('/');
            } else {
                return;
            }
        } else {
            setIsInitialLoad(false);
        }
         */
    /*
        if (!await validateAllFields())
            return;

        const ip = await inviteProfessional({defaultOrbit: orbitIdList, invitedRole, serviceLineId, organizationId, firstName, lastName, emailAddress}, (data) => {
            if (data?.error)
                return;

            navigate('/');
        })
        await dispatch(ip);
        */
  };

  useEffect(async () => {
    dispatch({
      type: REGISTER_ERROR,
      payload: "",
    });

    // Recover user.roles if missing (e.g. corrupted localStorage)
    if (user && !user.roles) {
      if (user.organizationId && user.organizations?.length > 0) {
        const currentOrg = user.organizations.find((org) => org.organizationId === user.organizationId);
        if (currentOrg?.roles) {
          user.roles = currentOrg.roles;
          setUser(user);
        }
      } else if (user.defaultOrbit && user.orbits?.length > 0) {
        const currentOrbit = user.orbits.find((o) => o.orbitId === user.defaultOrbit);
        if (currentOrbit?.roles) {
          user.roles = currentOrbit.roles;
          setUser(user);
        }
      }
    }

    if (user?.orbits?.length === 1) {
      const findCurrentOrbit = user?.orbits?.find(
        (orbit) => orbit.orbitId === user?.defaultOrbit,
      );
      if (findCurrentOrbit) {
        setOrbitId(user?.defaultOrbit);
        setOrganizationId(findCurrentOrbit.organizationId);
        setOrbitList(
          user?.orbits?.map((orbit) => {
            return {
              orbitId: orbit.orbitId,
              orbitName: orbit.name,
            };
          }),
        );
      } else if (user?.organizations?.length === 1) {
        const findCurrentOrg = user?.organizations?.find(
          (organization) => organization.organizationId === user?.organizationId,
        );
        if (findCurrentOrg) {
          setOrganizationId(findCurrentOrg.organizationId);
          if (findCurrentOrg.roles?.includes(ORBIT_ADMIN)) {
            setIsUserOrbitAdmin(true);
            setInvitedRole([ORBIT_ADMIN]);
          }
          setOrbitList(
            user?.orbits?.map((orbit) => {
              return {
                orbitId: orbit.orbitId,
                orbitName: orbit.name,
              };
            }),
          );
        }
      } else if (user?.organizations?.length > 1) {
        setOrganizationId(user?.organizationId);
      }
    } else if (user?.organizations?.length === 1) {
      const findCurrentOrbit = user?.organizations?.find(
        (organization) => organization.organizationId === user?.organizationId,
      );
      if (findCurrentOrbit) {
        setOrganizationId(findCurrentOrbit.organizationId);

        if (findCurrentOrbit.roles?.includes(ORBIT_ADMIN)) {
          setIsUserOrbitAdmin(true);
          setInvitedRole([ORBIT_ADMIN]);
        }
      }
    } else if (user?.orbits?.length > 1 && user?.roles?.includes(ORBIT_ADMIN)) {
      setOrganizationId(user?.organizationId);
      const filteredOrbits = user?.orbits?.filter(
        (orbit) => orbit.organizationId === user?.organizationId,
      );
      setOrbitList(
        filteredOrbits?.map((orbit) => {
          return {
            orbitId: orbit.orbitId,
            orbitName: orbit.name,
          };
        }),
      );
    } else if (user?.organizations?.length > 1) {
      setOrganizationId(user?.organizationId);
    }

    if (!user?.roles?.includes(REGULAR_USER)) {
      loadOrganization();
    }

    if (
      !user?.roles?.includes(REGULAR_USER) &&
      organizationId &&
      organizationId.length > 0
    ) {
      const ro = await rolesList({ organizationId }, async (data) => {
        setRoleList(
          data?.data?.map((item) => ({
            roleId: item.roleId,
            name: item.name.replaceAll("_", " "),
          })),
        );
        /*
                await swapList(data?.data?.map((item) => ({
                    roleId: item.roleId,
                    name: item.name.replaceAll('_', ' ')
                })), roleTempList);
                 */
      });
      await dispatch(ro);
    }

    if (
      user?.isSysAdmin ||
      user?.roles?.includes(SYS_ADMIN) ||
      user?.roles?.includes(ORGANIZATION_ADMIN) ||
      user?.roles?.includes(SERVICE_LINE_ADMIN) ||
      user?.roles?.includes(ORBIT_ADMIN)
    ) {
      setFirstNameLabel("Admin First Name");
      setLastNameLabel("Admin Last Name");
      setFirstNameHelperText("");
      setLastNameHelperText("");
      setEmailAddressHelperText("");
      setTitleText("Admin");
      setTitleTextTop("an Admin");
      setSubTitleText("admin");

      /*
            let organizationIdToUse = (organizationId && organizationId.length > 0 ? organizationId : undefined);
            const ro = await rolesList({ organizationId: organizationIdToUse }, async (data) => {
                setRoleList(data?.map((item) => ({roleId: item.roleId, name: item.name.replaceAll('_', ' ')})));
                await swapList(data?.map((item) => ({roleId: item.roleId, name: item.name.replaceAll('_', ' ')})), roleTempList);
            });

            await dispatch(ro);
             */
    } else {
      setFirstNameLabel("First Name");
      setLastNameLabel("Last Name");
      setFirstNameHelperText("");
      setLastNameHelperText("");
      setEmailAddressHelperText("");
      setTitleText("Family Member");
      setTitleTextTop("a Family Member");
      setSubTitleText("family member");

      const rl = await relativeList({}, (data) => {
        setRelationList(data.data);
      });

      await dispatch(rl);
    }
  }, []);

  useEffect(() => {
    (async () => {
      if (
        user.roles &&
        !user?.roles?.includes(REGULAR_USER) &&
        organizationId &&
        organizationId.length > 0
      ) {
        const dl = await diagnosisUserList({ organizationId }, (data) => {
          setDiagnosisList(data.data);
        });
        await dispatch(dl);
      }

      if (
        user.roles &&
        !user.roles.includes(REGULAR_USER)
      ) {
        if (organizationId && organizationId.length > 0) {
          const ro = await rolesList({ organizationId }, async (data) => {
            setRoleList(
              data?.data?.map((item) => ({
                roleId: item.roleId,
                name: item.name.replaceAll("_", " "),
              })),
            );
            /*
                        await swapList(data?.data?.map((item) => ({
                            roleId: item.roleId,
                            name: item.name.replaceAll('_', ' ')
                        })), roleTempList);
                         */
          });
          await dispatch(ro);

          const selectedOrganization = user?.organizations?.find((org) => org.organizationId === organizationId);
          const filterByList = (user?.roles?.includes(SERVICE_LINE_ADMIN) && selectedOrganization?.serviceLines?.length > 0)
              ? selectedOrganization.serviceLines
              : undefined;

          const loadOrbitsByOrganization = async () => {
            setServiceLineList([]);
            setServiceLineListModified(false);
            const orb = await orbit({ organizationId }, async (data) => {
              setOrbitList(data?.orbits);
            });
            await dispatch(orb);
          };

          try {
            const od = await getServiceLineList(
              {
                organizationId: organizationId
                  ? organizationId
                  : user.organizationId,
                filterByList,
              },
              async (data) => {
                if (
                  data &&
                  Array.isArray(data?.items) &&
                  data?.items &&
                  data?.items?.length === 1 &&
                  user?.roles?.includes(SERVICE_LINE_ADMIN)
                ) {
                  setServiceLineId([data.items[0].serviceLineId]);
                  setServiceLineList([]);
                  setServiceLineListModified(false);
                } else if (
                  data &&
                  Array.isArray(data?.items) &&
                  data?.items &&
                  data?.items?.length > 0
                ) {
                  /*
                  if (!data.items.some(item => item["name"] === 'Do not filter'))
                    data.items.push({serviceLineId: '', name: 'Do not filter'});
                   */
                  setServiceLineList(data?.items);
                  setServiceLineListModified(true);
                } else {
                  if (user?.roles?.includes(ORBIT_ADMIN) && user?.orbits?.length) {
                    const filteredOrbits = user?.orbits?.filter(
                      (orbit) => orbit.organizationId === organizationId,
                    );
                    if (filteredOrbits?.length > 0) {
                      setOrbitList(
                        filteredOrbits.map((orbit) => ({
                          orbitId: orbit.orbitId,
                          orbitName: orbit.name,
                        })),
                      );
                    } else {
                      await loadOrbitsByOrganization();
                    }
                  } else {
                    await loadOrbitsByOrganization();
                  }
                }
              },
            );
            await dispatch(od);
          } catch (e) {
            if (user?.roles?.includes(ORBIT_ADMIN) && user?.orbits?.length) {
              const filteredOrbits = user?.orbits?.filter(
                (orbit) => orbit.organizationId === organizationId,
              );
              if (filteredOrbits?.length > 0) {
                setOrbitList(
                  filteredOrbits.map((orbit) => ({
                    orbitId: orbit.orbitId,
                    orbitName: orbit.name,
                  })),
                );
              } else {
                await loadOrbitsByOrganization();
              }
            } else {
              await loadOrbitsByOrganization();
            }
          }
        }
      }
    })();
  }, [organizationId]);

  useEffect(() => {
    (async () => {
      if (serviceLineId && serviceLineId.length > 0) {
        const orb = await orbit({ serviceLineId, organizationId }, async (data) => {
          setOrbitList(data?.orbits);
        });
        await dispatch(orb);
      }
    })();
  }, [serviceLineId]);

  useEffect(async () => {
    if (roleList && Array.isArray(roleList) && roleList.length > 0) {
      if (
        !serviceLineList ||
        !Array.isArray(serviceLineList) ||
        serviceLineList.length === 0
      ) {
        await swapList(roleList, roleTempList);
      } else {
        await swapList(roleTempList, roleList);
      }
    }
  }, [serviceLineList, serviceLineListModified]);

  const changeRelation = (e) => {
    (async () => {
      setRelation(e.target.value);
      await validateRelationField(e);
    })();
  };
  const changeOrbit = (e) => {
    (async () => {
      setOrbitId(e.target.value);
      await validateOrbitField(e);
    })();
  };
  const changeFirstName = (e) => {
    setFirstName(e.target.value);
  };
  const changeLastName = (e) => {
    setLastName(e.target.value);
  };
  const changeEmailAddress = (e) => {
    setEmailAddress(e.target.value);
  };
  const changeOrganization = (e) => {
    (async () => {
      setOrganizationId(e.target.value);
      await validateOrganizationField(e);
    })();
  };
  const changeServiceLine = (e) => {
    (async () => {
      setServiceLineId([e.target.value]);
      await validateServiceLineField(e);
    })();
  };
  const changeRole = (e) => {
    (async () => {
      setInvitedRole(e.target.value);
      await validateRoleField(e);
    })();
  };
  const changeDiagnosis = (e) => {
    (async () => {
      setDiagnoses([parseInt(e.target.value)]);
      await validateDiagnosisField(e);
    })();
  };

  const validateFirstNameField = async (e) => {
    await setIsDirty(true);
    await setFirstNameError(validateFirstName(e.target.value));
  };

  const validateLastNameField = async (e) => {
    await setIsDirty(true);
    await setLastNameError(validateLastName(e.target.value));
  };

  const validateEmailAddressField = async (e) => {
    await setIsDirty(true);
    await setEmailAddressError(validateEmailAddress(e.target.value));
  };

  const validateOrganizationField = async (e) => {
    if (user.roles && !user.roles.includes(REGULAR_USER) && Array.isArray(organizationList) && organizationList.length > 0) {
      await setIsDirty(true);
      await setOrganizationIdError(validateOrganization(e.target.value));
    }
  };

  const validateServiceLineField = async (e) => {
    if (
      serviceLineList &&
      serviceLineList.length > 0 &&
      (user.isSysAdmin ||
        (user.roles &&
          (user.roles.includes(SYS_ADMIN) ||
            user.roles.includes(ORGANIZATION_ADMIN) ||
            user.roles.includes(SERVICE_LINE_ADMIN))))
    ) {
      await setIsDirty(true);

      if (invitedRole && parseInt(invitedRole) === SERVICE_LINE_ADMIN)
        await setServiceLineError(validateServiceLine(e.target.value));
    }
  };

  const validateRoleField = async (e) => {
    if (
      user.roles &&
      !user.roles.includes(REGULAR_USER) &&
      !user.roles.includes(ORBIT_ADMIN)
    ) {
      await setIsDirty(true);
      await setRoleError(validateRole(e.target.value));
    }
  };

  const validateOrbitField = async (e) => {
    if (
      user.isSysAdmin ||
      (user.roles &&
        ((user.roles.includes(ORBIT_ADMIN) &&
          user.orbits &&
          user.orbits.length > 1) ||
          user.roles.includes(SYS_ADMIN) ||
          user.roles.includes(SERVICE_LINE_ADMIN) ||
          user.roles.includes(SERVICE_LINE_ADMIN)))
    ) {
      await setIsDirty(true);
      await setOrbitError(validateOrbit(e.target.value));
    }
  };

  const validateRelationField = async (e) => {
    if (user.roles && user.roles.includes(REGULAR_USER)) {
      await setIsDirty(true);
      await setRelationError(validateRelation(e.target.value));
    }
  };

  const validateDiagnosisField = async (e) => {
    if (
      diagnosisList &&
      diagnosisList.length > 0 &&
      (user.isSysAdmin ||
        (user.roles &&
          (user.roles.includes(SYS_ADMIN) ||
            user.roles.includes(ORGANIZATION_ADMIN))))
    ) {
      await setIsDirty(true);
      await setDiagnosesError(validateDiagnoses(e.target.value));
    }
  };

  const validateAllFields = async () => {
    await setIsDirty(true);
    const invalidFirstName = validateFirstName(firstName);
    const invalidLastName = validateLastName(lastName);
    const invalidEmailAddress = validateEmailAddress(emailAddress);
    let invalidRelation = "";
    let invalidServiceLine = "";
    let invalidateOrganization = "";
    let invalidDiagnosis = "";
    let invalidRole = "";
    let invalidOrbit = "";

    if (user.roles && !user.roles.includes(REGULAR_USER) && Array.isArray(organizationList) && organizationList.length > 0) {
      invalidateOrganization = validateOrganization(organizationId);
      setOrganizationIdError(invalidateOrganization);
    }

    if (
      serviceLineList &&
      serviceLineList.length > 0 &&
      (user.isSysAdmin ||
        (user.roles &&
          (user.roles.includes(SYS_ADMIN) ||
            user.roles.includes(ORGANIZATION_ADMIN) ||
            user.roles.includes(SERVICE_LINE_ADMIN))))
    ) {
      invalidServiceLine = validateServiceLine(serviceLineId);
      setServiceLineError(invalidServiceLine);
    }

    if (user.roles && user.roles.includes(REGULAR_USER)) {
      invalidRelation = validateRelation(relation);
      setRelationError(invalidRelation);
    }

    if (
      user.roles &&
      !user.roles.includes(REGULAR_USER) &&
      !user.roles.includes(ORBIT_ADMIN)
    ) {
      invalidRole = validateRole(invitedRole);
    }

    if (
      diagnosisList &&
      diagnosisList.length > 0 &&
      (user.isSysAdmin ||
        (user.roles &&
          (user.roles.includes(SYS_ADMIN) ||
            user.roles.includes(ORGANIZATION_ADMIN))))
    ) {
      invalidDiagnosis = validateDiagnoses(diagnoses);
      setDiagnosesError(invalidDiagnosis);
    }

    if (
      user.isSysAdmin ||
      (user.roles &&
        ((user.roles.includes(ORBIT_ADMIN) &&
          user.orbits &&
          user.orbits.length > 1) ||
          user.roles.includes(SYS_ADMIN) ||
          user.roles.includes(SERVICE_LINE_ADMIN) ||
          user.roles.includes(SERVICE_LINE_ADMIN)))
    ) {
      invalidOrbit = validateOrbit(orbitId);
      setOrbitError(invalidOrbit);
    }

    setFirstNameError(invalidFirstName);
    setLastNameError(invalidLastName);
    setEmailAddressError(invalidEmailAddress);
    setRoleError(invalidRole);

    if (
      invalidFirstName.length > 0 ||
      invalidLastName.length > 0 ||
      invalidEmailAddress.length > 0 ||
      invalidRelation.length > 0 ||
      invalidateOrganization.length > 0 ||
      invalidDiagnosis.length > 0 ||
      invalidRole.length > 0 ||
      invalidServiceLine.length > 0 ||
      invalidOrbit.length > 0
    ) {
      return false;
    } else {
      return true;
    }
  };

  return (
    <div className="w-100 gradient">
      <div className="w-100 adminInviteUI">
        <ToastContainer />
        <Container className="registration invite">
          <Box component="form" ref={formRef} onSubmit={submitForm} autoComplete="off">
            <div className="pageContainer">
              <h1 className="text-center title page-title">Invite {titleTextTop}</h1>
              { user.roles && (user.roles.includes(SYS_ADMIN) || user.roles.includes(ORGANIZATION_ADMIN) || user.roles.includes(SERVICE_LINE_ADMIN) || user.roles.includes(ORBIT_ADMIN)) && (
                  <Card className="w-100 adminInviteUICard">
                    <CardContent className="adminInviteUICardContent">
                      <div className="card-body">
                        <div className="formFlexContainer">
                          <h2 className="title">Select which version of the orbit this admin should receive</h2>
                          { user.roles && !hasRole(user?.roles, REGULAR_USER) && Array.isArray(organizationList) && organizationList.length > 0 && (
                              <div className="formInput">
                                <InputWithErrorMessage errorMessage={organizationIdError}>
                                  <Select multiple={false} native={true} autoWidth={true} className="w-100" name="organization" id="organization" key="organization" label="Organization" inputRef={orgNativeRef} value={organizationId} variant="standard" onChange={changeOrganization} onBlur={validateOrganizationField}>
                                    <option value="" key="organization0">** Select Organization **</option>
                                    {Array.isArray(organizationList) && organizationList?.length > 0 ? organizationList?.map((item, i) => (
                                          <option value={item.organizationId} key={"organization" + (i + 1)}>{item.orgName}</option>
                                        )): ""}
                                  </Select>
                                </InputWithErrorMessage>
                              </div>
                            )}
                          {user.roles && (hasRole(user?.roles, SYS_ADMIN) || hasRole(user?.roles, ORGANIZATION_ADMIN) || hasRole(user?.roles, SERVICE_LINE_ADMIN) || hasRole(user?.roles, ORBIT_ADMIN)) && organizationId && organizationId.length > 0 && Array.isArray(serviceLineList) && serviceLineList.length > 0 && (
                              <div className="formInput">
                                <InputWithErrorMessage errorMessage={serviceLineError}>
                                  <Select multiple={false} native={true} autoWidth={true} className="w-100" name="serviceLine" id="serviceLine" key="serviceLine" label="name" inputRef={serviceLineNativeRef} value={serviceLineId ? serviceLineId : ""} variant="standard" onChange={changeServiceLine} onBlur={validateServiceLineField}>
                                    <option value="" key="serviceLine0">** Select Service Line **</option>
                                    {Array.isArray(serviceLineList) && serviceLineList?.length > 0 ? serviceLineList?.map((item, i) => (
                                          <option value={item.serviceLineId} key={"serviceLine" + (i + 1)}>{item.name}</option>
                                        )) : ""}
                                  </Select>
                                </InputWithErrorMessage>
                              </div>
                            )}
                          {user.roles && (hasRole(user?.roles, SYS_ADMIN) || hasRole(user?.roles, ORGANIZATION_ADMIN) || hasRole(user?.roles, SERVICE_LINE_ADMIN) || hasRole(user?.roles, ORBIT_ADMIN)) && ((organizationId && organizationId.length > 0) || (organizationId && organizationId.length > 0 && serviceLineId && serviceLineId.length > 0)) && Array.isArray(orbitList) && orbitList.length > 0 && (
                              <div className="formInput">
                                <InputWithErrorMessage errorMessage={orbitError}>
                                  <Select multiple={false} native={true} autoWidth={true} className="w-100" name="orbit" id="orbit" key="orbit" label="orbitName" inputRef={orbitNativeRef} value={orbitId} variant="standard" onChange={changeOrbit} onBlur={validateOrbitField}>
                                    <option value="" key="orbit0">** Select Orbit **</option>
                                    {Array.isArray(orbitList) && orbitList?.length > 0 ? orbitList?.map((item, i) => (
                                          <option value={item.orbitId} key={"orbit" + (i + 1)}>{item.orbitName}</option>
                                        )) : ""}
                                  </Select>
                                </InputWithErrorMessage>
                              </div>
                            )}
                          {diagnosisList && diagnosisList.length > 0 && user.roles && (hasRole(user?.roles, SYS_ADMIN) || hasRole(user?.roles, ORGANIZATION_ADMIN) || hasRole(user?.roles, SERVICE_LINE_ADMIN) || hasRole(user?.roles, ORBIT_ADMIN)) && (
                              <div className="formInput">
                                <InputWithErrorMessage errorMessage={diagnosesError}>
                                  <Select multiple={false} native={true} autoWidth={true} className="w-100" name="diagnosis" id="diagnosis" key="diagnosis" label="Diagnosis" inputRef={diagnosisNativeRef} value={diagnoses} variant="standard" onChange={changeDiagnosis} onBlur={validateDiagnosisField}>
                                    <option value="">** Select Version **</option>
                                    {diagnosisList && diagnosisList.length > 0 ? diagnosisList?.map((item, i) => (
                                          <option value={item.diagnosisId} key={"diagnosis" + i}>{item.name}</option>
                                        )) : ""}
                                  </Select>
                                </InputWithErrorMessage>
                              </div>
                            )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
              {user.roles && (hasRole(user?.roles, SYS_ADMIN) || hasRole(user?.roles, ORGANIZATION_ADMIN) || hasRole(user?.roles, SERVICE_LINE_ADMIN)) && organizationId && organizationId.length > 0 && !isUserOrbitAdmin && (
                  <Card className="w-100 adminInviteUICard bottomCard">
                    <CardContent className="adminInviteUICardContent">
                      <div className="card-body">
                        <h2 className="title">Select Admin Level of Permission</h2>
                        <div className="formFlexContainer">
                          <div className="formInput">
                            <InputWithErrorMessage errorMessage={roleError}>
                              <Select multiple={false} native={true} autoWidth={true} className="w-100 roleSelect" name="invitedRole" id="invitedRole" key="invitedRole" label="Role" value={invitedRole} variant="standard" onChange={changeRole} onBlur={validateRoleField}>
                                <option value="" key="invitedRole0">** Select Admin Role **</option>
                                {Array.isArray(roleList) && roleList?.length > 0 ? roleList?.map((item, i) => (
                                      <option value={item.roleId} key={"invitedRole" + (i + 1)}>{item.name}</option>
                                    )) : ""}
                              </Select>
                            </InputWithErrorMessage>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

              <Card className="w-100 adminInviteUICard bottomCard">
                <CardContent className="adminInviteUICardContent">
                  <div className="card-body w-100 material-box flex-column">
                    <h2 className="title">Invite by Email</h2>
                    <div className="formFlexContainer">
                      <div className="formInput">
                        <InputWithErrorMessage errorMessage={firstNameError}>
                          <TextField className="w-100" name="firstName" id="firstName" label={firstNameLabel} variant="outlined" helperText={firstNameHelperText} value={firstName} onChange={changeFirstName} onBlur={validateFirstNameField}/>
                        </InputWithErrorMessage>
                      </div>
                      <div className="formInput">
                        <InputWithErrorMessage errorMessage={lastNameError}>
                          <TextField className="w-100" name="lastName" id="lastName" label={lastNameLabel} variant="outlined" helperText={lastNameHelperText} value={lastName} onChange={changeLastName} onBlur={validateLastNameField}/>
                        </InputWithErrorMessage>
                      </div>
                      {user.roles && hasRole(user?.roles, REGULAR_USER) && (
                        <div className="formInput">
                          <InputWithErrorMessage errorMessage={relationError}>
                            <Select multiple={false} native={true} autoWidth={true} className="w-100" name="relation" id="relation" key="relation" label="Relation" value={relation} variant="outlined" onChange={changeRelation} onBlur={validateRelationField}>
                              <option value={0} key="relationOption">Please specify your relationship to the family member you are inviting</option>
                              {relationList.map((item, i) => (
                                <option value={item.relativeId} key={"relationOption" + (i + 1)}>{item.name}</option>
                              ))}
                            </Select>
                          </InputWithErrorMessage>
                        </div>
                      )}
                      <div className="formInput">
                        <InputWithErrorMessage errorMessage={emailAddressError}>
                          <TextField className="w-100" name="email" id="email" type="email" label="Email" variant="outlined" helperText={emailAddressHelperText} value={emailAddress} onChange={changeEmailAddress} onBlur={validateEmailAddressField}/>
                        </InputWithErrorMessage>
                      </div>
                      <div className="buttonContainer loginButtonContainer">
                        <Button type="submit" variant="contained" className="loginUIComp center" disabled={firstNameError?.length > 0 || lastNameError?.length > 0 || emailAddressError?.length > 0}>Invite This {titleText}</Button>
                        <h2 className="text-center title">The {subTitleText} will receive an invitation link to activate their guide.</h2>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </Box>
        </Container>
      </div>
      <Modal open={open} onClose={handleClose} aria-labelledby="modal-modal-title" aria-describedby="modal-modal-description">
        <Box className="modalInvite">
          <Typography id="modal-modal-title" variant="h6" component="h2" className="modalHeadline">
            RE-INVITE USER
            <div className="modal-form">
              <span className="close-btn" onClick={handleClose}></span>
            </div>
          </Typography>
          <Typography id="modal-modal-description" className="modalCopy">
            This user has been invited to their guide but has not yet activated.
            Click "Re-issue user invitation" and the invitation will be re-sent.
            Or, select "Cancel" to go back.
          </Typography>
          <div className="buttonContainer loginButtonContainer sideBySideButtons">
            <Button variant="contained" className="loginUIComp" onClick={handleClick}>RE-ISSUE USER INVITATION</Button>
            <Button type="submit" variant="contained" className="loginUIComp" onClick={handleClose}>CANCEL</Button>
          </div>
        </Box>
      </Modal>
      <Modal open={alreadyActivatedOpen} onClose={handleAlreadyActivatedClose} aria-labelledby="modal-modal-title" aria-describedby="modal-modal-description">
        <Box className="modalInvite">
          <Typography id="modal-modal-title" variant="h6" component="h2" className="modalHeadline">
            THIS USER HAS ALREADY BEEN ACTIVATED
            <div className="modal-form">
              <span className="close-btn" onClick={handleAlreadyActivatedClose}></span>
            </div>
          </Typography>
          <Typography id="modal-modal-description" className="modalCopy">
            This user's email address and/or phone number has already been sent
            an invitation, and the user has successfully activated this orbit
            guide. You can not send this user another invite for this guide
            using that contact information. Please click the "Send an invitation
            to different contact information" to send a new invitation using
            another email address and/or phone number.
          </Typography>
          <div className="buttonContainer loginButtonContainer sideBySideButtons">
            <Button variant="contained" className="loginUIComp" onClick={handleAlreadyActivatedClick}>
              SEND AN INVITATION TO DIFFERENT CONTACT INFORMATION
            </Button>
          </div>
        </Box>
      </Modal>
      <div className="gradientBottom"></div>
    </div>
  );
};

export default PatientInvite;
